import React from 'react';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';

const buttonSx = { backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } };

let digipalsCache = { pals: [], incomingRequests: [], ready: false };

function Digipals({ embedded = false }) {
  const [pals, setPals] = React.useState(() => digipalsCache.pals);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [message, setMessage] = React.useState(null);
  const [incomingRequests, setIncomingRequests] = React.useState(() => digipalsCache.incomingRequests);
  const [collabPal, setCollabPal] = React.useState(null);
  const [collabName, setCollabName] = React.useState('');

  const load = React.useCallback(async () => {
    const [{ data: palsData }, { data: incomingData }] = await Promise.all([api.get('/v1/digipals'), api.get('/v1/digipals/requests/incoming')]);
    digipalsCache = { pals: palsData, incomingRequests: incomingData, ready: true };
    setPals(palsData);
    setIncomingRequests(incomingData);
  }, []);

  React.useEffect(() => { load().catch(() => {}); }, [load]);
  React.useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => api.get('/v1/digipals/search', { params: { q: query } }).then(({ data }) => setResults(data)).catch(() => setResults([])), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const sendRequest = async userId => {
    try {
      await api.post('/v1/digipals/requests', { userId });
      setMessage({ type: 'success', text: 'Digipal request sent.' });
      setQuery(''); setResults([]); await load();
    } catch (error) { setMessage({ type: 'error', text: getApiErrorMessage(error, 'Unable to send request.') }); }
  };

  const inviteToCollaborate = async () => {
    if (!collabPal || !collabName.trim()) return;
    try {
      await api.post('/v1/collaborative-tags/invites', { recipientUserId: collabPal.userId, name: collabName });
      setMessage({ type: 'success', text: 'Collaborative tag invitation sent.' });
      setCollabPal(null); setCollabName('');
    } catch (error) { setMessage({ type: 'error', text: getApiErrorMessage(error, 'Unable to send collaborative tag invitation.') }); }
  };

  const respond = async (notification, action) => {
    try {
      await api.post(`/v1/digipals/requests/${notification.relationshipId}/${action}`);
      if (notification.id) await api.post(`/v1/inbox/${notification.id}/read`);
      await load();
    } catch (error) { setMessage({ type: 'error', text: getApiErrorMessage(error, 'Unable to update request.') }); }
  };

  return <main className={embedded ? 'digipals-panel' : 'page-shell'}>
    {!embedded && <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>My Digipals</Typography>}
    {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
    <Card sx={{ maxWidth: 720, mb: 3 }}><CardContent sx={{ display: 'grid', gap: 2 }}>
      <Typography variant="h6" fontWeight="bold">Add a Digipal</Typography>
      <TextField label="Search by username" value={query} onChange={event => setQuery(event.target.value)} helperText="Email addresses are not used for discovery." />
      {results.map(result => <Card key={result.id} variant="outlined"><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Avatar>{result.username[0]?.toUpperCase()}</Avatar><Typography sx={{ flex: 1 }}>{result.username}</Typography><Button onClick={() => sendRequest(result.id)} sx={buttonSx}>Add</Button>
      </CardContent></Card>)}
    </CardContent></Card>
    <Card sx={{ maxWidth: 720, mb: 3 }}><CardContent sx={{ display: 'grid', gap: 1.5 }}>
      <Typography variant="h6" fontWeight="bold">Digipal requests</Typography>
      {incomingRequests.map(request => <BoxRequest key={request.id} item={{ id: request.id, relationshipId: request.id, message: `${request.requesterUsername} wants to become your Digipal.` }} onRespond={respond} />)}
      {incomingRequests.length === 0 && <Typography color="text.secondary">No pending requests.</Typography>}
    </CardContent></Card>
    <Card sx={{ maxWidth: 720 }}><CardContent sx={{ display: 'grid', gap: 1 }}>
      <Typography variant="h6" fontWeight="bold">My Digipals</Typography>
      {pals.map(pal => <React.Fragment key={pal.relationshipId}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BoxProfile pal={pal} /><Button onClick={() => { setCollabPal(pal); setCollabName(''); }} sx={{ color: '#222', fontWeight: 'bold' }}>Create shared tag</Button></Box>{collabPal?.userId === pal.userId && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pl: 1 }}><TextField size="small" label="Shared tag name" value={collabName} onChange={event => setCollabName(event.target.value)} /><Button variant="contained" onClick={inviteToCollaborate} disabled={!collabName.trim()} sx={buttonSx}>Invite</Button><Button onClick={() => setCollabPal(null)} sx={{ color: '#222', fontWeight: 'bold' }}>Cancel</Button></Box>}<Divider /></React.Fragment>)}
      {pals.length === 0 && <Typography color="text.secondary">You have no Digipals yet.</Typography>}
    </CardContent></Card>
  </main>;
}

function BoxProfile({ pal }) { return <Button component={Link} to={`/?scope=digipals&ownerId=${pal.userId}`} state={{ ownerUsername: pal.username, ownerAvatarUrl: pal.avatarUrl || '' }} sx={{ justifyContent: 'flex-start', color: '#222', textTransform: 'none', gap: 1.5, py: 1 }}><Avatar src={pal.avatarUrl || undefined}>{pal.username[0]?.toUpperCase()}</Avatar><span>{pal.username}</span></Button>; }
function BoxRequest({ item, onRespond }) { return <Card variant="outlined"><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, '&:last-child': { pb: 1.25 } }}><Avatar>{item.message[0]?.toUpperCase()}</Avatar><Typography sx={{ flex: 1 }}>{item.message}</Typography><Button onClick={() => onRespond(item, 'accept')} sx={buttonSx}>Accept</Button><Button onClick={() => onRespond(item, 'decline')} sx={{ color: '#222', fontWeight: 'bold' }}>Decline</Button></CardContent></Card>; }

export default Digipals;
